/**
 * Wat het platform namens de verhuurder verstuurt.
 *
 * Drie vragen, in de volgorde waarin ze worden gesteld:
 *
 * 1. **Is het aangekomen?** Het log staat bovenaan, met mislukte berichten eerst.
 *    Dat is de eis "foutzichtbaarheid" uit hoofdstuk 19: een bevestiging die niet is
 *    verstuurd, is een gast die niets weet, en dat hoort niet in een serverlog te
 *    staan.
 * 2. **Wanneer gaat wat de deur uit?** De automatiseringen, per gebeurtenis.
 * 3. **Wat staat erin?** De sjablonen, met de platformtekst als vertrekpunt.
 *
 * Wat dit scherm bewust NIET kan: een bericht opnieuw versturen met andere tekst,
 * of een verstuurd bericht aanpassen. Wat er is verstuurd, staat vast — dat is de
 * hele reden dat de tekst op het bericht wordt bewaard en niet als verwijzing
 * (§17.31).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { useFormatters } from '@siteshowroom/i18n/react'

import { useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import {
  AUTOMATION_TRIGGERS,
  MESSAGE_STATUSES,
  RECIPIENTS,
  SCHEDULED_TRIGGERS,
  listAutomations,
  listMessages,
  listTemplates,
  messagingErrorMessageKey,
  resetTemplate,
  saveAutomation,
  saveTemplate,
} from '../lib/messaging-client'
import type {
  AutomationRule,
  LoggedMessage,
  MessageStatus,
  MessageTemplate,
  MessagingFailure,
  MessagingResult,
} from '../lib/messaging-client'

function FailureNotice({ error }: { readonly error: MessagingFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(messagingErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

export function MessagesPage(): ReactElement {
  const t = useTranslate()

  const [residences, setResidences] = useState<readonly ResidenceOption[]>([])
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [messages, setMessages] = useState<readonly LoggedMessage[]>([])
  const [automations, setAutomations] = useState<readonly AutomationRule[]>([])
  const [templates, setTemplates] = useState<readonly MessageTemplate[]>([])
  const [filter, setFilter] = useState<MessageStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<MessagingFailure | null>(null)

  useEffect(() => {
    let cancelled = false
    void listResidences().then((result) => {
      if (cancelled || !result.ok) {
        return
      }
      setResidences(result.data)
      const first = result.data[0]
      setResidenceId(first === undefined ? null : first.id)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    const [log, rules, texts] = await Promise.all([
      listMessages(residenceId, filter),
      listAutomations(residenceId),
      listTemplates(),
    ])
    if (log.ok) {
      setMessages(log.data)
    } else {
      setError(log.error)
    }
    if (rules.ok) {
      setAutomations(rules.data)
    }
    if (texts.ok) {
      setTemplates(texts.data)
    }
  }, [residenceId, filter])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError(null)
    void load()
  }, [load])

  const run = useCallback(
    async (work: () => Promise<MessagingResult<unknown>>): Promise<void> => {
      setBusy(true)
      setError(null)
      const result = await work()
      if (!result.ok) {
        setError(result.error)
      }
      await load()
      setBusy(false)
    },
    [load],
  )

  const failed = messages.filter((message) => message.status === 'failed').length

  return (
    <section className="page" aria-labelledby="messages-heading">
      <div className="page__header">
        <h1 className="page__title" id="messages-heading">
          {t('dashboard.messages.title')}
        </h1>
        <button
          className="button"
          disabled={residenceId === null || busy}
          onClick={() => {
            void load()
          }}
          type="button"
        >
          {t('common.action.reload')}
        </button>
      </div>

      <p className="prose">{t('dashboard.messages.intro')}</p>

      {residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.media.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.map((residence) => (
              <option key={residence.id} value={residence.id}>
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {error !== null && <FailureNotice error={error} />}

      {/* Mislukte berichten zijn geen regel in een lijst maar iets wat gedaan moet
          worden; ze horen boven de lijst te staan en niet erin. */}
      {failed > 0 && (
        <div className="notice notice--error" role="alert">
          <p className="notice__title">{t('dashboard.messages.failedNotice', { count: failed })}</p>
        </div>
      )}

      <h2 className="page__sectionTitle">{t('dashboard.messages.log.title')}</h2>

      <label className="field field--inline">
        <span className="field__label">{t('dashboard.messages.log.filter')}</span>
        <select
          className="field__input"
          disabled={busy}
          onChange={(event) => {
            const next = MESSAGE_STATUSES.find((candidate) => candidate === event.target.value)
            setFilter(next ?? null)
          }}
          value={filter ?? ''}
        >
          <option value="">{t('dashboard.messages.log.all')}</option>
          {MESSAGE_STATUSES.map((status) => (
            <option key={status} value={status}>
              {t(`dashboard.messages.status.${status}`)}
            </option>
          ))}
        </select>
      </label>

      {messages.length === 0 ? (
        <p className="page__status" role="status">
          {t('dashboard.messages.log.empty')}
        </p>
      ) : (
        <ul className="message-list">
          {messages.map((message) => (
            <MessageRow key={message.id} message={message} />
          ))}
        </ul>
      )}

      {residenceId !== null && (
        <AutomationSection
          automations={automations}
          busy={busy}
          onSave={(input) => {
            void run(() => saveAutomation(residenceId, input))
          }}
        />
      )}

      <TemplateSection
        busy={busy}
        onReset={(key, locale) => {
          void run(() => resetTemplate(key, locale))
        }}
        onSave={(input) => {
          void run(() => saveTemplate(input))
        }}
        templates={templates}
      />
    </section>
  )
}

function MessageRow({ message }: { readonly message: LoggedMessage }): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()
  const [open, setOpen] = useState(false)

  const moment = message.sentAt ?? message.scheduledFor

  return (
    <li className="message" data-status={message.status}>
      <p className="message__head">
        <span className="message__status">{t(`dashboard.messages.status.${message.status}`)}</span>
        <span className="message__subject">{message.subject}</span>
      </p>
      <p className="message__meta">
        {t('dashboard.messages.log.line', {
          recipient: t(`dashboard.messages.recipient.${message.recipient}`),
          address: message.toAddress,
          moment: moment === '' ? '' : formatters.formatDateTime(moment),
        })}
        {message.attempts > 1 && (
          <span className="message__attempts">
            {t('dashboard.messages.log.attempts', { count: message.attempts })}
          </span>
        )}
      </p>

      {/* De fout staat er voluit: "kon niet worden verstuurd" zonder reden laat de
          verhuurder met een probleem zitten dat hij niet kan oplossen. */}
      {message.lastError !== '' && <code className="message__error">{message.lastError}</code>}

      <button
        className="button button--quiet"
        onClick={() => {
          setOpen((current) => !current)
        }}
        type="button"
      >
        {open ? t('dashboard.messages.log.hide') : t('dashboard.messages.log.show')}
      </button>
      {open && <pre className="message__body">{message.body}</pre>}
    </li>
  )
}

interface AutomationSectionProps {
  readonly automations: readonly AutomationRule[]
  readonly busy: boolean
  readonly onSave: (input: {
    trigger: AutomationRule['trigger']
    recipient: AutomationRule['recipient']
    offsetDays: number
    sendAtHour: number
    enabled: boolean
  }) => void
}

function AutomationSection({ automations, busy, onSave }: AutomationSectionProps): ReactElement {
  const t = useTranslate()

  return (
    <>
      <h2 className="page__sectionTitle">{t('dashboard.messages.automations.title')}</h2>
      <p className="prose">{t('dashboard.messages.automations.intro')}</p>

      <ul className="automation-list">
        {AUTOMATION_TRIGGERS.flatMap((trigger) =>
          RECIPIENTS.map((recipient) => {
            const rule = automations.find(
              (entry) => entry.trigger === trigger && entry.recipient === recipient,
            )
            if (rule === undefined) {
              return null
            }
            return <AutomationRow busy={busy} key={rule.id} onSave={onSave} rule={rule} />
          }),
        ).filter((node): node is ReactElement => node !== null)}
      </ul>
    </>
  )
}

function AutomationRow({
  rule,
  busy,
  onSave,
}: {
  readonly rule: AutomationRule
  readonly busy: boolean
  readonly onSave: AutomationSectionProps['onSave']
}): ReactElement {
  const t = useTranslate()
  const scheduled = SCHEDULED_TRIGGERS.includes(rule.trigger)

  const [offsetDays, setOffsetDays] = useState(rule.offsetDays)
  const [sendAtHour, setSendAtHour] = useState(rule.sendAtHour)

  function save(enabled: boolean, days = offsetDays, hour = sendAtHour): void {
    onSave({
      trigger: rule.trigger,
      recipient: rule.recipient,
      offsetDays: scheduled ? days : 0,
      sendAtHour: hour,
      enabled,
    })
  }

  return (
    <li className="automation">
      <label className="automation__toggle">
        <input
          checked={rule.enabled}
          disabled={busy}
          onChange={(event) => {
            save(event.target.checked)
          }}
          type="checkbox"
        />
        <span>
          <strong>{t(`dashboard.messages.trigger.${rule.trigger}`)}</strong>
          <span className="automation__recipient">
            {t(`dashboard.messages.recipient.${rule.recipient}`)}
          </span>
        </span>
      </label>

      {scheduled && (
        <label className="field field--inline">
          <span className="field__label">
            {rule.trigger === 'after_departure'
              ? t('dashboard.messages.automations.daysAfter')
              : t('dashboard.messages.automations.daysBefore')}
          </span>
          <input
            className="field__input field__input--narrow"
            disabled={busy || !rule.enabled}
            max={60}
            min={0}
            onBlur={() => {
              if (offsetDays !== rule.offsetDays) {
                save(rule.enabled, offsetDays)
              }
            }}
            onChange={(event) => {
              setOffsetDays(Number(event.target.value))
            }}
            type="number"
            value={offsetDays}
          />
        </label>
      )}

      {scheduled && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.messages.automations.hour')}</span>
          <input
            className="field__input field__input--narrow"
            disabled={busy || !rule.enabled}
            max={23}
            min={0}
            onBlur={() => {
              if (sendAtHour !== rule.sendAtHour) {
                save(rule.enabled, offsetDays, sendAtHour)
              }
            }}
            onChange={(event) => {
              setSendAtHour(Number(event.target.value))
            }}
            type="number"
            value={sendAtHour}
          />
        </label>
      )}
    </li>
  )
}

function TemplateSection({
  templates,
  busy,
  onSave,
  onReset,
}: {
  readonly templates: readonly MessageTemplate[]
  readonly busy: boolean
  readonly onSave: (input: {
    templateKey: string
    locale: string
    subject: string
    body: string
  }) => void
  readonly onReset: (templateKey: string, locale: string) => void
}): ReactElement {
  const t = useTranslate()
  const [selected, setSelected] = useState('')

  const current = templates.find((entry) => `${entry.templateKey}|${entry.locale}` === selected)

  return (
    <>
      <h2 className="page__sectionTitle">{t('dashboard.messages.templates.title')}</h2>
      <p className="prose">{t('dashboard.messages.templates.intro')}</p>

      <label className="field">
        <span className="field__label">{t('dashboard.messages.templates.pick')}</span>
        <select
          className="field__input"
          disabled={busy}
          onChange={(event) => {
            setSelected(event.target.value)
          }}
          value={selected}
        >
          <option value="">{t('dashboard.messages.templates.none')}</option>
          {templates.map((template) => (
            <option key={template.id} value={`${template.templateKey}|${template.locale}`}>
              {t(`dashboard.messages.template.${template.templateKey.replace('.', '_')}`)} (
              {template.locale}){template.isOverride ? ' ★' : ''}
            </option>
          ))}
        </select>
        <span className="field__hint">{t('dashboard.messages.templates.starHint')}</span>
      </label>

      {current !== undefined && (
        <TemplateEditor
          busy={busy}
          key={`${current.templateKey}|${current.locale}`}
          onReset={onReset}
          onSave={onSave}
          template={current}
        />
      )}
    </>
  )
}

function TemplateEditor({
  template,
  busy,
  onSave,
  onReset,
}: {
  readonly template: MessageTemplate
  readonly busy: boolean
  readonly onSave: (input: {
    templateKey: string
    locale: string
    subject: string
    body: string
  }) => void
  readonly onReset: (templateKey: string, locale: string) => void
}): ReactElement {
  const t = useTranslate()
  const [subject, setSubject] = useState(template.subject)
  const [body, setBody] = useState(template.body)
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  /**
   * Voegt een plaatshouder in op de plek van de cursor.
   *
   * Aan het eind plakken zou betekenen dat je hem daarna alsnog moet verslepen, en
   * midden in een zin is precies waar hij hoort. Staat de cursor nergens — het veld
   * heeft geen focus gehad — dan komt hij achteraan, wat de enige zinnige plek is.
   */
  function insertAtCursor(text: string): void {
    const field = bodyRef.current
    if (field === null) {
      setBody((current) => current + text)
      return
    }
    const start = field.selectionStart
    const end = field.selectionEnd
    setBody((current) => current.slice(0, start) + text + current.slice(end))
    // Na het invoegen staat de cursor achter wat er is ingevoegd, zodat doortypen
    // werkt zoals je verwacht. In een microtask, want de waarde is nog niet gezet.
    queueMicrotask(() => {
      field.focus()
      field.setSelectionRange(start + text.length, start + text.length)
    })
  }

  function submit(event: FormEvent): void {
    event.preventDefault()
    onSave({
      templateKey: template.templateKey,
      locale: template.locale,
      subject: subject.trim(),
      body: body.trim(),
    })
  }

  return (
    <form className="template-form" onSubmit={submit}>
      <label className="field">
        <span className="field__label">{t('dashboard.messages.templates.subject')}</span>
        <input
          className="field__input"
          disabled={busy}
          maxLength={200}
          onChange={(event) => {
            setSubject(event.target.value)
          }}
          type="text"
          value={subject}
        />
      </label>

      <label className="field">
        <span className="field__label">{t('dashboard.messages.templates.body')}</span>
        <textarea
          className="field__input field__input--mono"
          disabled={busy}
          maxLength={20_000}
          onChange={(event) => {
            setBody(event.target.value)
          }}
          ref={bodyRef}
          rows={14}
          value={body}
        />
        {/* Welke plaatshouders mogen, staat in de API en wordt daar gecontroleerd;
            een typefout komt terug als foutmelding bij het opslaan en niet als een
            leeg gat bij de gast. */}
        <span className="field__hint">{t('dashboard.messages.templates.placeholderHint')}</span>
      </label>

      <PlaceholderHelp
        names={template.placeholders}
        onInsert={(name) => {
          insertAtCursor(`{{${name}}}`)
        }}
      />

      <div className="template-form__actions">
        <button className="button button--primary" disabled={busy} type="submit">
          {t('dashboard.messages.templates.save')}
        </button>
        {template.isOverride && (
          <button
            className="button button--danger"
            disabled={busy}
            onClick={() => {
              if (window.confirm(t('dashboard.messages.templates.confirmReset'))) {
                onReset(template.templateKey, template.locale)
              }
            }}
            type="button"
          >
            {t('dashboard.messages.templates.reset')}
          </button>
        )}
      </div>
    </form>
  )
}

/**
 * De plaatshouders die in dit sjabloon mogen, met uitleg.
 *
 * Waarom dit een eigen blok is en geen zin onder het veld: een verhuurder die
 * `{{guest_name}}` ziet staan, weet wat dat betekent — maar `{{total}}` naast
 * `{{refund_amount}}`, of `{{arrival_info}}` naast `{{house_rules}}`, is raden. En
 * raden bij een tekst die naar een gast gaat, betekent dat hij hem niet aanpast.
 *
 * Bij elke plaatshouder staat waar de waarde vandaan komt. Dat is de vraag die er
 * meteen achteraan komt: "en waar vul ik dat dan in?"
 *
 * De lijst komt van de API en wordt hier niet nagebouwd — hij verschilt per sjabloon,
 * en een tweede kopie zou iets kunnen aanbieden wat niemand invult.
 */
function PlaceholderHelp({
  names,
  onInsert,
}: {
  readonly names: readonly string[]
  readonly onInsert: (name: string) => void
}): ReactElement | null {
  const t = useTranslate()

  if (names.length === 0) {
    return null
  }

  return (
    <details className="placeholders" open>
      <summary className="placeholders__summary">
        {t('dashboard.messages.placeholders.title', { count: names.length })}
      </summary>
      <p className="prose">{t('dashboard.messages.placeholders.intro')}</p>
      <ul className="placeholders__list">
        {names.map((name) => (
          <li className="placeholder" key={name}>
            <button
              className="placeholder__insert"
              onClick={() => {
                onInsert(name)
              }}
              title={t('dashboard.messages.placeholders.insert')}
              type="button"
            >
              {`{{${name}}}`}
            </button>
            <span className="placeholder__what">{t(`dashboard.messages.placeholder.${name}`)}</span>
            <span className="placeholder__where">
              {t(`dashboard.messages.placeholderSource.${name}`)}
            </span>
          </li>
        ))}
      </ul>
    </details>
  )
}
